import React, { RefObject } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  DragObjectWithType,
  DropTargetMonitor,
  useDrag,
  useDrop,
} from 'react-dnd'

import { ItemTypes } from 'data/constants'
import { selectors as ChannelsSelectors } from 'data/channels'
import { canWriteInChannel } from 'remote/channels/roles'
import { RowLayoutHoverZone } from '../RowLayout'
import { moveNotesTo, moveNoteToSibling } from 'data/notes/actions'
import { formatDateToDefaultPosition } from '../../helpers/formatDateToDefaultPosition'

const HOVER_ZONE_MARGIN = 10

export type NoteDragItem = DragObjectWithType & {
  data: {
    title: string
    noteId: string
    channelId: string
  }
  noteId: string
  channelId: string
  ancestorNoteIds: string[]
}

function getHoverDimensions(
  monitor: DropTargetMonitor,
  component: HTMLElement
) {
  const hoverBoundingRect = component.getBoundingClientRect()
  const hoverHeight = hoverBoundingRect.bottom - hoverBoundingRect.top
  const clientOffset = monitor.getClientOffset()
  const hoverClientY =
    (clientOffset ? clientOffset.y : 0) - hoverBoundingRect.top
  return { hoverClientY, hoverHeight }
}

function getHoverZone(monitor: DropTargetMonitor, component: HTMLElement) {
  const { hoverClientY, hoverHeight } = getHoverDimensions(monitor, component)
  if (hoverClientY < HOVER_ZONE_MARGIN) {
    return 'top'
  }
  if (hoverHeight - hoverClientY < HOVER_ZONE_MARGIN) {
    return 'bottom'
  }
  return 'middle'
}

export const useNoteDrag = (item: NoteDragItem) =>
  useDrag({
    item,
    collect(monitor) {
      return {
        isDragging: monitor.isDragging(),
      }
    },
  })

export const useNoteDrop = (
  dropItem: NoteDragItem,
  ref: RefObject<HTMLElement>,
  setHoverZone: (hoverZone: RowLayoutHoverZone) => void,
  hoverZone: RowLayoutHoverZone,
  onlyAllowDropInside?: boolean
) => {
  const canWriteSelector = React.useMemo(
    () => (state: any) => {
      const channel = ChannelsSelectors.getChannel(state, dropItem.channelId)
      const canWrite = canWriteInChannel({ channel })
      return canWrite
    },
    [dropItem.channelId]
  )
  const canWrite = useSelector(canWriteSelector)
  const dispatch = useDispatch()

  const canDrop = (dragItem: NoteDragItem) => {
    // Prevent dropping a note onto itself
    if (dropItem.noteId === dragItem.noteId) {
      return false
    }

    // Checking user right to drop
    if (!canWrite) {
      return false
    }

    // Prevent dropping a parent note to one of its children
    return !dropItem.ancestorNoteIds.includes(dragItem.noteId)
  }

  return useDrop({
    accept: ItemTypes.NOTE,

    collect(monitor) {
      return {
        isDragOver: monitor.canDrop() && monitor.isOver({ shallow: true }),
      }
    },

    canDrop,

    hover(dragItem, monitor) {
      const component = ref.current
      if (!component || !canDrop(dragItem)) {
        return
      }
      const hoverZone = onlyAllowDropInside
        ? 'middle'
        : getHoverZone(monitor, component)
      setHoverZone(hoverZone)
    },

    drop(dragItem, monitor) {
      // Prevent dropping on parent in a nested dnd environ
      if (!monitor.isOver({ shallow: true })) {
        return
      }

      if (hoverZone === 'middle') {
        dispatch(
          moveNotesTo({
            noteIds: [dragItem.noteId],
            parentNoteId: dropItem.noteId,
            channelId: dropItem.channelId,
            position: formatDateToDefaultPosition(),
            analyticsSource: 'dnd',
            toaster: false,
          })
        )
        return
      }

      dispatch(
        moveNoteToSibling(
          dragItem.noteId,
          dropItem.noteId,
          dropItem.channelId,
          hoverZone
        )
      )
    },
  })
}

export const useNoteDragAndDrop = (
  noteId: string,
  channelId: string,
  ancestorNoteIds: string[],
  title: string,
  ref: RefObject<HTMLElement>,
  onlyAllowDropInside?: boolean
) => {
  const item: NoteDragItem = {
    type: ItemTypes.NOTE,
    // used in edit/DndPlugin
    data: {
      title,
      noteId,
      channelId,
    },
    noteId,
    channelId,
    ancestorNoteIds,
  }

  const [hoverZone, setHoverZone] = React.useState<RowLayoutHoverZone>('top')

  const [{ isDragOver }, drop] = useNoteDrop(
    item,
    ref,
    setHoverZone,
    hoverZone,
    onlyAllowDropInside
  )

  const [{ isDragging }, drag] = useNoteDrag(item)

  drag(drop(ref))

  return { isDragOver, isDragging, hoverZone }
}
